import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { Control, Errors } from 'react-redux-form';

import 'react-datepicker/dist/react-datepicker.css';
import './styles/DateSelector.scss';

/**
 * Wraps the DatePicker component from react-datepicker with a localized react-redux-form text control.
 *
 * The model should be a string of the format "YYYY-MM-DD".
 *
 * @param model the model path as passed to any RRF control
 * @param modelValue the ISO 8601 string value for the date, i.e. "2017-06-19"
 * @param controlProps pass-through control properties
 * @param mapProps pass-through mapped properties
 * @param isClearable pass-through boolean value, by default the field is clearable
 * @param validators validator functions
 * @param errorMessages pass-through object where keys are validator functions and values are corresponding text messages
 * @param props pass-through properties to the underlying RRF control
 */
const DateSelector = ({
  model,
  modelValue,
  controlProps,
  mapProps,
  isClearable,
  validators,
  errorMessages,
  defaultValue,
  ...props
}) => {
  const dateFormat = global.I18n.date.formats.default_date_fns;

  const [dateObject, setDateObject] = useState(modelValue ? moment(modelValue) : null);
  const [dateValue, setDateValue] = useState(
    modelValue && dateObject ? dateObject.format(global.I18n.date.formats.default_moment) : ''
  );

  return (
    <div>
      <Control.custom
        {...props}
        model={model}
        component={DatePicker}
        controlProps={{
          dateFormat,
          isClearable,
          modelValue: dateValue,
          defaultValue: defaultValue,
          selected: dateObject && dateObject.toDate(),
          ...controlProps
        }}
        validators={validators}
        mapProps={{
          onChange: ({ onChange }) => (date) => {
            if (date) {
              const momentDate = moment(date);
              onChange(momentDate.format('YYYY-MM-DD'));
              setDateValue(momentDate.format(global.I18n.date.formats.default_moment));
              setDateObject(momentDate);
            } else {
              onChange(null);
              setDateValue('');
              setDateObject(null);
            }
          },
          autoComplete: 'off',
          name: ({ model: nameModel }) => nameModel,
          ...mapProps
        }}
      />
      {errorMessages && (
        <Errors
          model={model}
          messages={errorMessages}
          show='touched'
          className='text-danger'
        />
      )}
    </div>
  );
};

DateSelector.propTypes = {
  model: PropTypes.string,
  modelValue: PropTypes.string,
  controlProps: PropTypes.object,
  mapProps: PropTypes.object,
  isClearable: PropTypes.bool,
  validators: PropTypes.object,
  errorMessages: PropTypes.object
};

DateSelector.defaultProps = {
  model: null,
  modelValue: '',
  controlProps: {},
  mapProps: {},
  isClearable: true,
  validators: {},
  errorMessages: null
};

export default DateSelector;
